import React, { useEffect, useMemo, useState } from 'react';

import { listAudit } from './operations';
import { formatWhen, groupAuditChanges, toAuditCsvData } from './utils';
import { ExportCsvButton, ModalShell } from '../../components';
import { newDateFormatted } from '../../utils';

import './styles.scoped.css';

/**
 * CP-857: change history for a code table.
 *
 * Opened either for the whole table (toolbar) or scoped to one row (from the grid), which
 * is why it takes an optional `keyValue`.
 *
 * Deletes are the reason this exists: both tables are hard-deleted so nothing downstream
 * needs an IsActive filter, and this is where a removed row's values are recovered from.
 *
 * Exportable, because the reason to read a quarter of history is usually to feed it to
 * something else rather than to scroll this table.
 */

// A blank value has to render as something visible, or "cleared the notes" looks like
// nothing happened at all.
const BLANK = '—';

// sp_cl_cp_bi_codetable_audit_list caps its result at @Top, defaulting to 500, so an
// unbounded history cannot time out the API Lambda. It caps CHANGES rather than rows, so a
// change is never split -- but hitting the cap still has to be visible, or an export that
// held only the most recent 500 changes would be read as complete.
const AUDIT_CHANGE_CAP = 500;

const ChangeDetail = ({ changes }) => {
  if (!changes.length) {
    return <span className="audit-nochange">no field values changed</span>;
  }

  return (
    <ul className="audit-changes">
      {changes.map(({ label, from, to }) => (
        <li key={label}>
          <span className="audit-field">{label}</span>
          {from !== null && <span className="audit-from">{from || BLANK}</span>}
          {from !== null && to !== null && <span className="audit-arrow">→</span>}
          {to !== null && <span className="audit-to">{to || BLANK}</span>}
        </li>
      ))}
    </ul>
  );
};

const AuditModal = ({ config, keyValue, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    listAudit(config, { keyValue })
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [config, keyValue]);

  const entries = useMemo(() => groupAuditChanges(rows), [rows]);
  const csvData = useMemo(() => toAuditCsvData(rows), [rows]);
  const csvFileName = useMemo(
    () => `${config.type.toLowerCase()}-history-${newDateFormatted.toString()}.csv`,
    [config.type],
  );

  return (
    <ModalShell
      title={`${config.title} — Change History`}
      subtitle={keyValue || 'Most recent changes across the whole list.'}
      buttons={[{ text: 'Close', onPress: onClose }]}>
      {loading && <p className="audit-empty">Loading…</p>}

      {!loading && !!error && <p className="form-error">{error}</p>}

      {!loading && !error && !entries.length && <p className="audit-empty">No changes recorded yet.</p>}

      {!loading && !error && !!entries.length && (
        <div className="audit-toolbar">
          <p className="audit-count">
            {entries.length} change{entries.length === 1 ? '' : 's'}
            {entries.length >= AUDIT_CHANGE_CAP &&
              ` (the most recent ${AUDIT_CHANGE_CAP} — older changes are not shown)`}
          </p>
          {/* One CSV row per changed field, so "when did this change" is a filter rather
              than a diff. See toAuditCsvData. */}
          <ExportCsvButton data={csvData} filename={csvFileName} />
        </div>
      )}

      {!loading && !error && !!entries.length && (
        <div className="audit-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>{config.keyLabel}</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.ChangeId}>
                  <td>{formatWhen(entry.ChangedAt)}</td>
                  <td>{entry.ChangedBy}</td>
                  <td>{entry.ChangeAction}</td>
                  <td>{entry.KeyValue}</td>
                  <td>
                    <ChangeDetail changes={entry.changes} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  );
};

export default AuditModal;
